/**
 * @fileoverview Agent Status badge with hover explanation.
 *
 * Purpose: The run-state chip on the voidable / offences master tables and
 * focused-head headers (Live / Updating / Locked / Stale / Blocked /
 * Not run). When the agent cannot run, the tooltip shows the missing input
 * from the review-window basis note instead of a bare "Blocked". Missing
 * relation-back / appointment details deep-link to Job Settings so the
 * practitioner can fill them in without hunting for the tab.
 */

import { Badge } from "@/ui/primitives/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import { Loader2, Settings } from "lucide-react";
import { Link, useParams } from "@tanstack/react-router";
import {
  agentStatusTooltip,
  agentText,
  blockedSettingsFix,
  STATUS_BADGE,
  type HeadStatus,
} from "./flaggedShared";

export function AgentStatusBadge({
  status,
  basisNote,
}: {
  status: HeadStatus;
  basisNote?: string;
}) {
  const params = useParams({ strict: false });
  const jobId = "jobId" in params ? params.jobId : undefined;
  const runState = STATUS_BADGE[status];
  const tooltip = agentStatusTooltip({ status, basisNote });
  const settingsFix = blockedSettingsFix(status, basisNote);
  const badge = (
    <Badge color={runState.color} variant="soft" size="sm">
      {status === "updating" && (
        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
      )}
      {runState.label}
    </Badge>
  );
  if (!tooltip) return badge;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-help">{badge}</span>
        </TooltipTrigger>
        <TooltipContent className="flex max-w-sm flex-col items-start gap-1.5">
          <p>{agentText(tooltip)}</p>
          {settingsFix && jobId ? (
            <Link
              to="/dashboard/jobs/$jobId/settings"
              params={{ jobId }}
              search={{ tab: settingsFix.tab }}
              className="inline-flex items-center gap-1 font-medium text-primary underline-offset-2 hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              <Settings className="size-3" />
              {settingsFix.label}
            </Link>
          ) : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
